import { atom } from 'nanostores';
import type { AppBundle } from '@/utils/app-bundles';
import { getAppBundleFromUrl } from '@/utils/navigation';

export interface LocalSearchResult {
  id: string;
  label: string;
  description?: string;
  icon?: string;
  onSelect: () => void | Promise<void>;
}

export type LocalSearchHandler = (query: string) => LocalSearchResult[];

export interface LocalSearchOptions {
  heading?: string;
}

export interface SearchProviderEntry {
  handler: LocalSearchHandler;
  app: AppBundle | null;
  providerId: string;
  heading: string;
}

let nextRegistrationId = 0;

export const localSearchAtom = atom<Map<string, SearchProviderEntry>>(new Map());

function unregister(key: string): void {
  const current = new Map(localSearchAtom.get());
  current.delete(key);
  localSearchAtom.set(current);
}

export function registerSearch(
  providerId: string,
  handler: LocalSearchHandler,
  options?: LocalSearchOptions,
): () => void {
  const id = nextRegistrationId++;
  const key = `${id}-${providerId}`;
  const app = getAppBundleFromUrl(window.location.pathname);
  const heading = options?.heading ?? 'Results';
  const entry: SearchProviderEntry = { handler, app, providerId, heading };

  const current = new Map(localSearchAtom.get());
  // Replace only existing registration for this providerId in this app, so that
  // re-registering (e.g. updating the handler) doesn't duplicate, but a different
  // app using the same providerId does not remove the other app's handler.
  const keysToDelete = [...current.entries()]
    .filter(([, entry]) => entry.providerId === providerId && entry.app === app)
    .map(([k]) => k);

  for (const k of keysToDelete) {
    current.delete(k);
  }

  current.set(key, entry);
  localSearchAtom.set(current);

  return () => unregister(key);
}

// Expose to window (follows local-commands pattern)
Object.assign(window, {
  __hydra_local_search__: {
    registerSearch,
  } satisfies Window['__hydra_local_search__'],
});
